import axios, { AxiosInstance, AxiosError } from 'axios';
import type { ExpertPageProfile, ExpertService, AvailabilityData, BookingResponse, ClientBooking } from '../types';
import type {
  Schedule,
  ScheduleException,
  HolidaySetting,
  Holiday,
  CreateScheduleRequest,
  UpdateScheduleRequest,
  CreateExceptionRequest,
  UpdateExceptionRequest,
  UpdateHolidaySettingsRequest,
} from '../types/working-hours';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Client API instance - for end-users booking services
export const clientApi: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/api/v1/client`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Expert API instance - for solo masters offering services
export const expertApi: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/api/v1/expert`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Public API instance - for unauthenticated expert page access
export const publicApi: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/api/v1/public`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Response interceptor to handle 429 errors and prevent unnecessary redirects
const handle429Error = (error: AxiosError) => {
  if (error.response?.status === 429) {
    // Don't redirect to login on rate limit errors
    // Just return the error to be handled by the component
    console.warn('Rate limit exceeded. Please slow down.');
    return Promise.reject(error);
  }

  // For other errors, pass them through
  return Promise.reject(error);
};

// Add interceptor to all API instances
clientApi.interceptors.response.use(
  (response) => response,
  handle429Error
);

expertApi.interceptors.response.use(
  (response) => response,
  handle429Error
);

publicApi.interceptors.response.use(
  (response) => response,
  handle429Error
);

// Error response type
export interface ApiError {
  error: {
    code: string;
    message: string;
    details?: Record<string, string[]>;
  };
}

// Available profiles info
export interface AvailableProfiles {
  client: boolean;
  expert: boolean;
}

// Auth response types
export interface ClientAuthResponse {
  client: {
    id: string;
    email: string;
    locale: string;
    created_at: string;
  };
  message?: string;
  available_profiles?: AvailableProfiles;
}

export interface ExpertAuthResponse {
  expert: {
    id: string;
    email: string;
    display_name: string;
    timezone: string;
    account_id: string;
    created_at: string;
  };
  message?: string;
  available_profiles?: AvailableProfiles;
}

// Client Portal types
export interface ExpertSummary {
  slug: string;
  displayName: string;
  isFavorite: boolean;
}

export interface Service {
  id: string;
  name: string;
  duration: number;
  price: string;
  currency: string;
}

export interface ExpertProfile extends ExpertSummary {
  services: Service[];
}

export interface Notification {
  id: string;
  type: string;
  title: string;
  body?: string;
  createdAt: string;
  read: boolean;
  bookingId?: string;
  expertSlug?: string;
  occursAt?: string;
}

export interface NotificationPage {
  items: Notification[];
  nextCursor?: string;
  unreadCount: number;
}

export interface ClientProfile {
  email: string;
  name?: string;
  phone?: string;
  locale: string;
  timezone?: string;
  phone_verified?: boolean;
  unconfirmed_email?: string | null;
}

// Phone Verification Response
export interface PhoneVerificationResponse {
  message: string;
  phone: string;
  verification_token?: string;
}

// Google OAuth Response
export interface GoogleOAuthResponse {
  user: {
    id: string;
    email: string;
    phone?: string | null;
  };
  client?: {
    id: string;
    email: string;
    name: string;
    locale: string;
    created_at: string;
  };
  expert?: {
    id: string;
    display_name: string;
    timezone: string;
    created_at: string;
  };
  message: string;
  // When profile creation is needed
  requires_profile?: boolean;
  user_exists?: boolean;
  suggested_name?: string;
  email?: string;
  // When phone is missing (OAuth users)
  requires_phone?: boolean;
}

// Phone Completion Response
export interface PhoneCompletionResponse {
  message: string;
  user: {
    id: string;
    email: string;
    phone: string;
    phone_verified: boolean;
  };
}

// Client Auth API
export const clientAuthApi = {
  signup: (data: {
    email: string;
    phone: string;
    phone_verification_token: string;
    password: string;
    password_confirmation: string;
    locale?: string
  }) =>
    clientApi.post<ClientAuthResponse>('/auth/signup', { client: data }),

  signin: (data: { email: string; password: string }) =>
    clientApi.post<ClientAuthResponse>('/auth/signin', data),

  signout: () =>
    clientApi.delete('/auth/signout'),

  forgot: (data: { email: string }) =>
    clientApi.post('/auth/forgot', data),

  reset: (data: { token: string; password: string; password_confirmation: string }) =>
    clientApi.post('/auth/reset', {
      reset_password_token: data.token,
      password: data.password,
      password_confirmation: data.password_confirmation,
    }),

  session: () =>
    clientApi.get<ClientAuthResponse>('/auth/session'),

  // Phone verification
  sendPhoneCode: (phone: string) =>
    clientApi.post<PhoneVerificationResponse>('/auth/phone/send_code', { phone }),

  verifyPhoneCode: (phone: string, code: string) =>
    clientApi.post<PhoneVerificationResponse>('/auth/phone/verify_code', { phone, code }),

  // Phone completion (for OAuth users)
  completePhone: (phone: string, code: string) =>
    clientApi.post<PhoneCompletionResponse>('/auth/phone/complete', { phone, code }),

  // Google OAuth
  googleAuth: (data: { id_token?: string; access_token?: string; locale?: string }) =>
    clientApi.post<GoogleOAuthResponse>('/auth/google', data),
};

// Expert Auth API
export const expertAuthApi = {
  signup: (data: {
    email: string;
    phone: string;
    phone_verification_token: string;
    password: string;
    password_confirmation: string;
    display_name: string;
    timezone?: string;
  }) =>
    expertApi.post<ExpertAuthResponse>('/auth/signup', { expert: data }),

  signin: (data: { email: string; password: string }) =>
    expertApi.post<ExpertAuthResponse>('/auth/signin', data),

  signout: () =>
    expertApi.delete('/auth/signout'),

  forgot: (data: { email: string }) =>
    expertApi.post('/auth/forgot', data),

  reset: (data: { token: string; password: string; password_confirmation: string }) =>
    expertApi.post('/auth/reset', {
      reset_password_token: data.token,
      password: data.password,
      password_confirmation: data.password_confirmation,
    }),

  session: () =>
    expertApi.get<ExpertAuthResponse>('/auth/session'),

  // Phone verification
  sendPhoneCode: (phone: string) =>
    expertApi.post<PhoneVerificationResponse>('/auth/phone/send_code', { phone }),

  verifyPhoneCode: (phone: string, code: string) =>
    expertApi.post<PhoneVerificationResponse>('/auth/phone/verify_code', { phone, code }),

  // Phone completion (for OAuth users)
  completePhone: (phone: string, code: string) =>
    expertApi.post<PhoneCompletionResponse>('/auth/phone/complete', { phone, code }),

  // Google OAuth
  googleAuth: (data: { id_token?: string; access_token?: string; display_name?: string; timezone?: string }) =>
    expertApi.post<GoogleOAuthResponse>('/auth/google', data),
};

// Error mapper for forms
export function toFormErrors(error: any): Record<string, string> {
  const apiError = error.response?.data as ApiError | undefined;

  if (!apiError?.error) {
    return { _general: 'An unexpected error occurred' };
  }

  // Map field-specific errors
  if (apiError.error.details) {
    const formErrors: Record<string, string> = {};
    Object.entries(apiError.error.details).forEach(([field, messages]) => {
      formErrors[field] = messages[0];
    });
    return formErrors;
  }

  // Map general error
  return { _general: apiError.error.message };
}

// Error code mapper for user-friendly messages
export function getErrorMessage(error: any): string {
  // Handle network errors
  if (!error.response) {
    return 'Network error. Please check your connection.';
  }

  // Handle non-JSON responses
  if (typeof error.response.data === 'string') {
    return 'Server error. Please try again later.';
  }

  // Handle errors array (from validation errors)
  if (error.response.data?.errors && Array.isArray(error.response.data.errors)) {
    // Remove duplicates and join with newlines
    const uniqueErrors = Array.from(new Set(error.response.data.errors));
    return uniqueErrors.join('\n');
  }

  // Handle simple error messages (like from our auth endpoints)
  if (error.response.data?.error && typeof error.response.data.error === 'string') {
    return error.response.data.error;
  }

  const apiError = error.response?.data as ApiError | undefined;

  if (!apiError?.error?.code) {
    return error.response.data?.message || 'An unexpected error occurred. Please try again.';
  }

  const errorMessages: Record<string, string> = {
    invalid_credentials: 'Email or password is incorrect.',
    locked: 'Your account is locked. Check your email to unlock.',
    unconfirmed: 'Please confirm your email address before logging in.',
    rate_limited: 'Too many attempts. Please try again later.',
    rate_limit_exceeded: 'Too many requests. Please slow down and try again in a moment.',
    validation_error: apiError.error.message || 'Please check your input.',
  };

  return errorMessages[apiError.error.code] || apiError.error.message || 'An error occurred.';
}

// Client Portal API
export const clientPortalApi = {
  experts: {
    search: (q: string) =>
      clientApi.get<ExpertSummary[]>('/experts/search', { params: { q } }),

    listAdded: () =>
      clientApi.get<ExpertSummary[]>('/experts/added'),

    get: (slug: string) =>
      clientApi.get<ExpertProfile>(`/experts/${slug}`),
  },

  favorites: {
    list: () =>
      clientApi.get<ExpertSummary[]>('/favorites'),

    add: (expertSlug: string) =>
      clientApi.post('/favorites', { expertSlug }),

    remove: (expertSlug: string) =>
      clientApi.delete(`/favorites/${expertSlug}`),
  },

  notifications: {
    list: (params?: { page?: number; pageSize?: number }) =>
      clientApi.get<NotificationPage>('/notifications', { params }),

    markRead: (id: string) =>
      clientApi.post(`/notifications/${id}/mark_read`),

    markAllRead: () =>
      clientApi.post('/notifications/mark_all_read'),
  },

  profile: {
    get: () =>
      clientApi.get<ClientProfile>('/profile'),

    update: (data: Partial<ClientProfile> & { phone_verification_token?: string }) =>
      clientApi.patch<ClientProfile>('/profile', data),

    delete: () =>
      clientApi.delete('/profile'),
  },

  emailChange: {
    sendCode: (new_email: string) =>
      clientApi.post<{ message: string }>('/email_change/send_code', { new_email }),

    verify: (code: string, new_email: string) =>
      clientApi.post('/email_change/verify', { code, new_email }),

    cancel: () =>
      clientApi.delete('/email_change'),
  },

  bookings: {
    list: (filter?: 'upcoming' | 'past') =>
      clientApi.get<ClientBooking[]>('/bookings', { params: { filter } }),

    listByExpert: (expertSlug: string, filter?: 'upcoming' | 'past') =>
      clientApi.get<ClientBooking[]>('/bookings', { params: { filter, expert_slug: expertSlug } }),

    get: (id: string) =>
      clientApi.get<ClientBooking>(`/bookings/${id}`),

    create: (data: {
      expert_slug: string;
      service_id: string;
      slot_start: string;
      notes?: string;
    }) =>
      clientApi.post<BookingResponse>('/bookings', data),

    cancel: (id: string, cancellation_reason?: string) =>
      clientApi.patch<{ message: string; booking: ClientBooking }>(`/bookings/${id}/cancel`, { cancellation_reason }),
  },
};

// Public Expert Page API
export const publicExpertApi = {
  getProfile: (expertSlug: string) =>
    publicApi.get<ExpertPageProfile>(`/experts/${expertSlug}`),

  getServices: (expertSlug: string, params?: { active_only?: boolean }) =>
    publicApi.get<{ expert: { slug: string; display_name: string }; services: ExpertService[] }>(
      `/experts/${expertSlug}/services`,
      { params }
    ),

  getAvailability: (
    expertSlug: string,
    params: {
      service_id: string;
      mode: 'day' | 'week' | 'month';
      date: string;
      step_min?: number;
      horizon_days?: number;
      limit?: number;
    }
  ) =>
    publicApi.get<AvailabilityData>(`/experts/${expertSlug}/availability`, { params }),
};

// Slug check response type
export interface SlugCheckResponse {
  available: boolean;
  error?: string;
  suggestions?: string[];
}

// Google Calendar types
export interface GoogleCalendar {
  id: string;
  summary: string;
  description?: string;
  primary: boolean;
  background_color: string;
  access_role: string;
}

export interface IntegrationInfo {
  kind: string;
  name: string;
  description: string;
  status: 'connected' | 'needs_attention' | 'disconnected' | 'pending';
  connected: boolean;
  needs_attention: boolean;
  error_message?: string;
  calendar_id?: string;
  last_synced_at?: string;
}

// Public phone verification response
export interface PublicPhoneVerificationResponse {
  message?: string;
  phone: string;
  verified?: boolean;
  token?: string;
  expires_in?: number;
}

// Public booking response
export interface PublicBookingResponse {
  booking: {
    id: string;
    status: string;
    starts_at: string;
    ends_at: string;
    notes?: string;
    client: {
      name: string;
      email: string;
      phone: string;
    };
    items: Array<{
      service: {
        id: string;
        name: string;
      };
      expert: {
        id: string;
        display_name: string;
      };
      price_cents: number;
      duration_min: number;
    }>;
    total_price_cents: number;
  };
}

// Public Phone Verification API (for booking flow)
export const publicPhoneApi = {
  sendCode: (phone: string) =>
    publicApi.post<PublicPhoneVerificationResponse>('/phone/send_code', { phone }),

  verifyCode: (phone: string, code: string) =>
    publicApi.post<PublicPhoneVerificationResponse>('/phone/verify_code', { phone, code }),
};

// Public Booking API (for unauthenticated bookings)
export const publicBookingApi = {
  create: (data: {
    service_id: string;
    provider_id: string;
    starts_at: string;
    notes?: string;
    client: {
      name: string;
      email: string;
      phone: string;
    };
  }) =>
    publicApi.post<PublicBookingResponse>('/bookings', { booking: data }),
};

// Expert Dashboard API
export const expertDashboardApi = {
  profile: {
    get: () =>
      expertApi.get('/profile'),

    update: (data: {
      display_name?: string;
      phone?: string;
      phone_verification_token?: string;
      timezone?: string;
      locale?: string;
      color?: string;
      currency?: string;
      is_bookable?: boolean;
      slug?: string;
      use_seasonal_schedules?: boolean;
    }) =>
      expertApi.patch('/profile', { profile: data }),

    checkSlug: (slug: string) =>
      expertApi.get<SlugCheckResponse>('/profile/check_slug', { params: { slug } }),
  },

  emailChange: {
    sendCode: (new_email: string) =>
      expertApi.post<{ message: string }>('/email_change/send_code', { new_email }),

    verify: (code: string, new_email: string) =>
      expertApi.post('/email_change/verify', { code, new_email }),

    cancel: () =>
      expertApi.delete('/email_change'),
  },

  calendar: {
    getAvailability: (params: {
      view_mode: 'day' | 'week' | 'month';
      date: string;
      timezone?: string;
    }) =>
      expertApi.get('/calendar/availability', { params }),
  },

  bookings: {
    list: (params?: {
      filter?: 'all' | 'upcoming' | 'past' | 'cancelled';
      search?: string;
      status?: string;
      service_id?: string;
      sort?: 'date' | 'status' | 'client';
      order?: 'asc' | 'desc';
    }) =>
      expertApi.get('/bookings', { params }),

    get: (id: string) =>
      expertApi.get(`/bookings/${id}`),

    create: (data: {
      expert_client_id: string;
      service_id: string;
      starts_at: string;
      notes?: string;
    }) =>
      expertApi.post('/bookings', { booking: data }),

    update: (id: string, data: {
      starts_at?: string;
      ends_at?: string;
      notes?: string;
    }) =>
      expertApi.patch(`/bookings/${id}`, { booking: data }),

    confirm: (id: string) =>
      expertApi.post(`/bookings/${id}/confirm`),

    cancel: (id: string, cancellation_reason?: string) =>
      expertApi.post(`/bookings/${id}/cancel`, { cancellation_reason }),

    complete: (id: string) =>
      expertApi.post(`/bookings/${id}/complete`),

    markNoShow: (id: string) =>
      expertApi.post(`/bookings/${id}/mark_no_show`),

    delete: (id: string) =>
      expertApi.delete(`/bookings/${id}`),
  },

  clients: {
    list: (params?: {
      page?: number;
      per_page?: number;
      sort?: 'name' | 'recent_booking' | 'total_spent';
      order?: 'asc' | 'desc';
      search?: string;
    }) =>
      expertApi.get('/clients', { params }),

    search: (query: string, limit?: number) =>
      expertApi.post('/clients/search', { query, limit }),

    get: (id: string) =>
      expertApi.get(`/clients/${id}`),
  },

  expertClients: {
    list: (params?: {
      page?: number;
      per_page?: number;
      sort?: 'name' | 'recent_booking' | 'total_spent';
      order?: 'asc' | 'desc';
      filter?: 'all' | 'linked' | 'unlinked' | 'favorites';
    }) =>
      expertApi.get('/expert_clients', { params }),

    search: (query: string, limit?: number) =>
      expertApi.post('/expert_clients/search', { query, limit }),

    recent: (limit?: number) =>
      expertApi.get('/expert_clients/recent', { params: { limit } }),

    get: (id: string) =>
      expertApi.get(`/expert_clients/${id}`),

    create: (data: {
      display_name: string;
      custom_email?: string;
      custom_phone?: string;
      private_notes?: string;
      tags?: string[];
      is_favorite?: boolean;
    }) =>
      expertApi.post('/expert_clients', data),

    update: (id: string, data: {
      display_name?: string;
      custom_email?: string;
      custom_phone?: string;
      private_notes?: string;
      tags?: string[];
      is_favorite?: boolean;
    }) =>
      expertApi.patch(`/expert_clients/${id}`, data),

    delete: (id: string) =>
      expertApi.delete(`/expert_clients/${id}`),

    toggleFavorite: (id: string) =>
      expertApi.post(`/expert_clients/${id}/toggle_favorite`),
  },

  services: {
    list: (params?: { active_only?: boolean }) =>
      expertApi.get('/services', { params }),

    get: (id: string) =>
      expertApi.get(`/services/${id}`),

    create: (data: {
      name: string;
      description?: string | null;
      duration_min: number;
      padding_before_min?: number;
      padding_after_min?: number;
      price_cents: number;
      deposit_cents?: number;
      currency?: string;
      active?: boolean;
    }) =>
      expertApi.post('/services', { service: data }),

    update: (id: string, data: {
      name?: string;
      description?: string | null;
      duration_min?: number;
      padding_before_min?: number;
      padding_after_min?: number;
      price_cents?: number;
      deposit_cents?: number;
      currency?: string;
      active?: boolean;
    }) =>
      expertApi.patch(`/services/${id}`, { service: data }),

    delete: (id: string) =>
      expertApi.delete(`/services/${id}`),
  },

  integrations: {
    list: () =>
      expertApi.get('/integrations'),

    getGoogleCalendar: () =>
      expertApi.get('/integrations/google_calendar'),

    getGoogleOAuthUrl: () => {
      const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
      const redirectUri = `${window.location.origin}/app/integrations/google-calendar/callback`;
      const params = new URLSearchParams({
        client_id: clientId,
        redirect_uri: redirectUri,
        response_type: 'code',
        scope: 'https://www.googleapis.com/auth/calendar',
        access_type: 'offline',
        prompt: 'consent',
      });
      return `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
    },

    connectGoogle: (data: { code: string; redirect_uri: string }) =>
      expertApi.post('/integrations/google_calendar/connect', data),

    disconnectGoogle: () =>
      expertApi.delete('/integrations/google_calendar/disconnect'),

    listGoogleCalendars: () =>
      expertApi.get<{ calendars: GoogleCalendar[] }>('/integrations/google_calendar/calendars'),

    selectCalendar: (calendarId: string) =>
      expertApi.post('/integrations/google_calendar/select_calendar', { calendar_id: calendarId }),

    createGoogleCalendar: (name: string = 'BookDay') =>
      expertApi.post<{ message: string; calendar: { id: string; summary: string } }>(
        '/integrations/google_calendar/create_calendar',
        { name }
      ),

    syncGoogle: () =>
      expertApi.post('/integrations/google_calendar/sync'),
  },

  schedules: {
    list: () =>
      expertApi.get<{ schedules: Schedule[] }>('/schedules'),

    get: (id: string) =>
      expertApi.get<{ schedule: Schedule }>(`/schedules/${id}`),

    create: (data: CreateScheduleRequest) =>
      expertApi.post<{ schedule: Schedule }>('/schedules', { schedule: data }),

    update: (id: string, data: UpdateScheduleRequest) =>
      expertApi.patch<{ schedule: Schedule }>(`/schedules/${id}`, { schedule: data }),

    delete: (id: string) =>
      expertApi.delete(`/schedules/${id}`),

    setDefault: (id: string) =>
      expertApi.post<{ schedule: Schedule }>(`/schedules/${id}/set_default`),

    previewConflicts: (data: { schedule_id?: string; working_hours?: any[]; exceptions?: any[] }) =>
      expertApi.post<{ conflicts: any[]; has_conflicts: boolean }>('/schedules/preview_conflicts', data),
  },

  scheduleExceptions: {
    list: (params?: { from?: string; to?: string; recurring?: boolean }) =>
      expertApi.get<{ exceptions: ScheduleException[] }>('/schedule_exceptions', { params }),

    get: (id: string) =>
      expertApi.get<{ exception: ScheduleException }>(`/schedule_exceptions/${id}`),

    create: (data: CreateExceptionRequest) =>
      expertApi.post<{ exception: ScheduleException }>('/schedule_exceptions', { schedule_exception: data }),

    update: (id: string, data: UpdateExceptionRequest) =>
      expertApi.patch<{ exception: ScheduleException }>(`/schedule_exceptions/${id}`, { schedule_exception: data }),

    delete: (id: string) =>
      expertApi.delete(`/schedule_exceptions/${id}`),
  },

  holidaySettings: {
    get: () =>
      expertApi.get<{ holiday_setting: HolidaySetting }>('/holiday_settings'),

    update: (data: UpdateHolidaySettingsRequest) =>
      expertApi.patch<{ holiday_setting: HolidaySetting }>('/holiday_settings', { holiday_setting: data }),

    getHolidays: (year?: number) =>
      expertApi.get<{ holidays: Holiday[]; region_code: string; enabled: boolean }>('/holiday_settings/holidays', { params: { year } }),

    getUpcoming: (count?: number) =>
      expertApi.get<{ holidays: Holiday[]; region_code: string; enabled: boolean }>('/holiday_settings/upcoming', { params: { count } }),
  },
};
